import { useEffect, useMemo, useRef, useState } from 'react';
import type { CostReport, SpendView } from '@alfred/shared';
import { api, describeError } from './api.js';

const RANGES = [7, 30, 90] as const;
const SERIES = [
  { key: 'ai', label: 'AI', color: 'var(--series-1)' },
  { key: 'tools', label: 'Tools', color: 'var(--series-2)' },
  { key: 'phone', label: 'Phone', color: 'var(--series-3)' },
] as const;
type Day = CostReport['days'][number];

const usd = (n: number) => `$${n.toFixed(n > 0 && n < 0.1 ? 3 : 2)}`;

/** What Alfred has cost: today against the cap, recent totals, and a daily breakdown. */
export function CostsView({ spend }: { spend: SpendView | undefined }) {
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [report, setReport] = useState<CostReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asTable, setAsTable] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .costs(days)
      .then((r) => live && (setReport(r), setError(null)))
      .catch((err: unknown) => live && setError(describeError(err)));
    return () => {
      live = false;
    };
  }, [days]);

  return (
    <div className="view">
      <div className="tiles">
        <div className="tile">
          <span className="tile-label">Today</span>
          <span className="tile-value">{usd(report?.totals.today ?? spend?.spentUsd ?? 0)}</span>
          {spend && <span className="tile-sub">of {usd(spend.capUsd)} daily limit</span>}
        </div>
        <div className="tile">
          <span className="tile-label">Last 7 days</span>
          <span className="tile-value">{usd(report?.totals.last7 ?? 0)}</span>
        </div>
        <div className="tile">
          <span className="tile-label">Last {days} days</span>
          <span className="tile-value">{usd(report?.totals.period ?? 0)}</span>
        </div>
      </div>

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Daily spend</h2>
          <div className="filters" role="tablist" aria-label="Range">
            {RANGES.map((r) => (
              <button
                key={r}
                role="tab"
                aria-selected={days === r}
                className={`chip-btn ${days === r ? 'chip-btn-on' : ''}`}
                onClick={() => setDays(r)}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
        <ul className="legend" aria-label="Legend">
          {SERIES.map((s) => (
            <li key={s.key}>
              <span className="swatch" style={{ background: s.color }} />
              {s.label}
            </li>
          ))}
        </ul>
        {error && <p className="error">{error}</p>}
        {report && (asTable ? <DayTable days={report.days} /> : <StackedBars days={report.days} />)}
        <button className="link-btn" onClick={() => setAsTable((t) => !t)}>
          {asTable ? 'Show chart' : 'Show as table'}
        </button>
      </section>

      {report && (
        <div className="split">
          <Breakdown title="By model" rows={report.byModel} />
          <Breakdown title="By tool" rows={report.byTool} />
        </div>
      )}
    </div>
  );
}

/** Stacked daily bars: AI at the base, then tools, then phone. Hover or focus a day for detail. */
function StackedBars({ days }: { days: Day[] }) {
  const [active, setActive] = useState<number | null>(null);
  // Draw at the real pixel width so axis text stays readable on a phone.
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(720);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(
      ([e]) => e && setW(Math.max(260, Math.round(e.contentRect.width))),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = W < 480 ? 180 : 220;
  const pad = { top: 12, right: 8, bottom: 24, left: 44 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;

  const max = useMemo(() => niceMax(Math.max(0.01, ...days.map((d) => d.total))), [days]);
  const band = plotW / days.length;
  const barW = Math.max(2, Math.min(28, band * 0.7));
  const y = (v: number) => (v / max) * plotH;
  const ticks = [0, max / 2, max];
  const labelEvery = Math.ceil(days.length / Math.max(2, Math.floor(plotW / 70)));

  return (
    <div className="chart" ref={box} onMouseLeave={() => setActive(null)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Daily spend, stacked by AI, tools and phone"
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={pad.left}
              x2={W - pad.right}
              y1={pad.top + plotH - y(t)}
              y2={pad.top + plotH - y(t)}
              className="grid"
            />
            <text x={pad.left - 6} y={pad.top + plotH - y(t) + 4} className="axis" textAnchor="end">
              {usd(t)}
            </text>
          </g>
        ))}
        {days.map((d, i) => {
          const x = pad.left + i * band + (band - barW) / 2;
          let base = pad.top + plotH;
          const segments = SERIES.map((s) => ({ ...s, value: d[s.key] })).filter(
            (s) => s.value > 0,
          );
          return (
            <g key={d.date} opacity={active === null || active === i ? 1 : 0.45}>
              {segments.map((s, j) => {
                const h = Math.max(1, y(s.value));
                const top = base - h;
                const isTop = j === segments.length - 1;
                // 2px surface gap between stacked segments.
                const gap = j > 0 ? 2 : 0;
                const path = isTop
                  ? roundedTop(x, top, barW, h - gap, Math.min(4, barW / 2, h - gap))
                  : `M${x},${top}h${barW}v${h - gap}h${-barW}z`;
                base = top;
                return <path key={s.key} d={path} fill={s.color} />;
              })}
              {i % labelEvery === 0 && (
                <text x={x + barW / 2} y={H - 6} className="axis" textAnchor="middle">
                  {shortDate(d.date)}
                </text>
              )}
              {/* Hit target: the whole column, bigger than the bar. */}
              <rect
                x={pad.left + i * band}
                y={pad.top}
                width={band}
                height={plotH}
                fill="transparent"
                tabIndex={0}
                aria-label={`${longDate(d.date)}: ${usd(d.total)}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
              />
            </g>
          );
        })}
      </svg>
      {active !== null && days[active] && (
        <div
          className="tooltip"
          style={{
            left: `${((pad.left + active * band + band / 2) / W) * 100}%`,
          }}
        >
          <strong>{longDate(days[active].date)}</strong>
          {SERIES.map((s) => (
            <span key={s.key} className="tt-row">
              <span className="swatch" style={{ background: s.color }} />
              {s.label}
              <span className="tt-val">{usd(days[active]?.[s.key] ?? 0)}</span>
            </span>
          ))}
          <span className="tt-row tt-total">
            Total<span className="tt-val">{usd(days[active].total)}</span>
          </span>
        </div>
      )}
    </div>
  );
}

function DayTable({ days }: { days: Day[] }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Day</th>
            <th>AI</th>
            <th>Tools</th>
            <th>Phone</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {[...days].reverse().map((d) => (
            <tr key={d.date}>
              <td>{longDate(d.date)}</td>
              <td>{usd(d.ai)}</td>
              <td>{usd(d.tools)}</td>
              <td>{usd(d.phone)}</td>
              <td>{usd(d.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: { name: string; costUsd: number }[] }) {
  const max = Math.max(0.0001, ...rows.map((r) => r.costUsd));
  return (
    <section className="panel">
      <h2 className="panel-title">{title}</h2>
      {rows.length === 0 && <p className="muted small">Nothing yet.</p>}
      <ul className="breakdown">
        {rows.map((r) => (
          <li key={r.name}>
            <span className="bd-name">{r.name.replace(/^claude-/, '')}</span>
            <span className="bd-bar">
              <span style={{ width: `${(r.costUsd / max) * 100}%` }} />
            </span>
            <span className="bd-val">{usd(r.costUsd)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function roundedTop(x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.max(0, r);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

/** A round axis maximum just above v: 1, 2, 2.5, 5 or 10 × 10^n. */
function niceMax(v: number) {
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  const step = [1, 2, 2.5, 5, 10].find((s) => v / exp <= s) ?? 10;
  return step * exp;
}

const parse = (date: string) => new Date(`${date}T12:00:00`);
const shortFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });
const longFmt = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
const shortDate = (d: string) => shortFmt.format(parse(d));
const longDate = (d: string) => longFmt.format(parse(d));
